import type { RoleOnboardingValue } from '@b-stock/account-api-client'

import appConfig from '@config/config'
import { get, patch } from '@helpers/xhr'
import type { FreightForwardState } from '@pages/businessProfile/freightForwarder/workflow'

const url = appConfig.api.accounts.id.roles.accountsIdRole.onboarding

export type PatchData = {
  [key: string]: {
    completion?: number
    wasSkipped?: boolean
    additionalProperties: {
      [key: string]: {
        historyStack: string[] | never[]
        currentStep: string
        state: FreightForwardState
      }
    }
  }
}

// Mirrors fe-core's `WorkflowState`, which each page parameterises with its own
// state and step-enum types.
type OnboardingWorkflow<State = unknown, Step extends string = string> = {
  historyStack: Step[]
  currentStep: Step
  state: State
}

// The response is keyed by module name. The generated `RoleOnboardingValue`
// types `additionalProperties` as an opaque `object`, so mirror the shape we
// actually read: an open bag carrying the workflow snapshot alongside progress
// bookkeeping (`step`, `length`, `lastUsed` — see `saveProgress` in
// `@store/data/modules/progress`). Each caller supplies its own workflow state
// type; it defaults to `unknown` so untyped readers can't treat it as `any`.
type OnboardingModule<State = unknown, Step extends string = string> = Omit<
  RoleOnboardingValue,
  'additionalProperties'
> & {
  additionalProperties: {
    workflow?: OnboardingWorkflow<State, Step>
    [key: string]: unknown
  }
}

type OnboardingData<State = unknown, Step extends string = string> = Record<
  string,
  OnboardingModule<State, Step>
>

export default {
  load: async <State = unknown, Step extends string = string>({
    id,
    role,
  }: {
    id: string
    role: string
  }) => {
    const { data } = await get<OnboardingData<State, Step>>(url, null, {
      entities: { id, role },
    })
    return data
  },

  save: ({ id, role, data }: { id: string; role: string; data: PatchData }) =>
    patch(url, data, {
      entities: { id, role },
    }),
}
